import React, { useEffect, useState, useMemo, useRef } from "react";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa6";
import { useHotelContext } from "hooks/hotel/useHotelContext";
import HotelCard from "./HotelCard";
import SectionHeading from "component/Common/SectionHeading";
import useToggle from "hooks/useToggle";
import { BiFilterAlt } from "react-icons/bi";
import MobileFilters from "./FiltersDrawer";
import { useTranslation } from "react-i18next";

const HotelSearchWrapper = () => {
    const { availabilityResults, availabilityLoading, availabilityError } =
        useHotelContext();
    const [offset, setOffset] = useState(0);
    const [limit] = useState(20);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPage] = useState(0);
    const [paginatedHotels, setPaginatedHotels] = useState([]);
    const { isOpen: isOpenFilters, toggle: toggleFilters } = useToggle();
    const [filteredHotels, setFilteredHotels] = useState([]);
    const elementRef = useRef(null);
    const { t } = useTranslation();

    useEffect(() => {
        if (availabilityResults?.hotels) {
            setFilteredHotels(availabilityResults.hotels);
        }
    }, [availabilityResults]);

    useEffect(() => {
        const totalhotels = filteredHotels?.length || 0;
        setTotalPage(Math.max(1, Math.ceil(totalhotels / limit)));
        setPaginatedHotels(filteredHotels?.slice(offset, offset + limit) || []);
    }, [filteredHotels, offset, limit]);

    useEffect(() => {
        setOffset(0);
        setPage(1);
    }, [filteredHotels]);

    useEffect(() => {
        // Scroll to the element when component mounts
        if (elementRef.current) {
            elementRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
                inline: "nearest",
            });
        }
    }, []);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
            setOffset((newPage - 1) * limit);
            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        }
    };

    const renderPagination = useMemo(() => {
        const maxVisiblePages = 5;
        const pages = [];

        if (totalPages <= maxVisiblePages + 2) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(
                    <li
                        key={i}
                        className="page-item mb-0"
                        onClick={() => handlePageChange(i)}
                    >
                        <span
                            className={`page-link pagination-link-active ${
                                i === page ? "bg-main-color text-white" : ""
                            }`}
                        >
                            {i}
                        </span>
                    </li>
                );
            }
        } else {
            pages.push(
                <li
                    key={1}
                    className="page-item mb-0"
                    onClick={() => handlePageChange(1)}
                >
                    <span
                        className={`page-link pagination-link-active ${
                            1 === page ? "bg-main-color text-white" : ""
                        }`}
                    >
                        1
                    </span>
                </li>
            );

            if (page > maxVisiblePages) {
                pages.push(
                    <li key="start-dots" className="page-item mb-0 disabled">
                        <span className="page-link pagination-link-active">
                            ...
                        </span>
                    </li>
                );
            }

            const startPage = Math.max(
                2,
                page - Math.floor(maxVisiblePages / 2)
            );
            const endPage = Math.min(
                totalPages - 1,
                startPage + maxVisiblePages - 1
            );

            for (let i = startPage; i <= endPage; i++) {
                pages.push(
                    <li
                        key={i}
                        className="page-item mb-0"
                        onClick={() => handlePageChange(i)}
                    >
                        <span
                            className={`page-link pagination-link-active ${
                                i === page ? "bg-main-color text-white" : ""
                            }`}
                        >
                            {i}
                        </span>
                    </li>
                );
            }

            if (endPage < totalPages - 1) {
                pages.push(
                    <li key="end-dots" className="page-item mb-0 disabled">
                        <span className="page-link pagination-link-active">
                            ...
                        </span>
                    </li>
                );
            }

            pages.push(
                <li
                    key={totalPages}
                    className="page-item mb-0"
                    onClick={() => handlePageChange(totalPages)}
                >
                    <span
                        className={`page-link pagination-link-active ${
                            totalPages === page
                                ? "bg-main-color text-white"
                                : ""
                        }`}
                    >
                        {totalPages}
                    </span>
                </li>
            );
        }

        return pages;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, totalPages]);

    return (
        <section id="explore_area" className="section_padding_top">
            {availabilityError && (
                <div className="mx-auto w-full max-w-[1440px] px-3 lg:px-0 h-screen">
                    <div className="flex rounded-md bg-red-100 border-2 text-red-700 border-red-700 p-4 w-full items-center">
                        {t("sorryNoHotelsFound")}
                    </div>
                </div>
            )}
            {!availabilityError && (
                <div
                    ref={elementRef}
                    className="mx-auto w-full max-w-[1440px] px-3 lg:px-0"
                >
                    {availabilityLoading ? (
                        <SectionHeading heading={t("")} />
                    ) : (
                        <SectionHeading
                            heading={
                                t("hotelsFound") +" " + filteredHotels?.length || 0
                            }
                        />
                    )}
                    <div className="row   ps-3 pe-0 md:pe-3 my-4 block  w-full">
                        {availabilityResults?.hotels && (
                            <div
                                onClick={toggleFilters}
                                className="flex gap-1 items-center justify-center px-4 py-1 font-semibold cursor-pointer bg-secondary-color text-white w-full  md:w-fit border border-secondary-color  rounded-md"
                            >
                                <BiFilterAlt />
                                {t("filters")}
                            </div>
                        )}
                    </div>
                    <div className="row">
                        {availabilityLoading
                            ? [1, 2, 3, 4, 5, 6, 7, 8].map((_, index) => (
                                  <div
                                      className="col-xl-3 col-lg-4 col-md-6 col-sm-6 col-12 mb-4"
                                      key={index}
                                  >
                                      <div className="theme_common_box_two img_hover h-full">
                                          <div className="theme_two_box_img h-[200px]">
                                              <div className="hotel-card-skeleton">
                                                  <span className="skeletonLoading"></span>
                                              </div>
                                          </div>
                                          <div className="theme_two_box_content p-4">
                                              <div className="hotel-card-skeleton h-6 mb-2 w-3/4">
                                                  <span className="skeletonLoading"></span>
                                              </div>
                                              <div className="hotel-card-skeleton h-4 mb-3 w-1/2">
                                                  <span className="skeletonLoading"></span>
                                              </div>
                                              <div className="hotel-card-skeleton h-5 w-1/3">
                                                  <span className="skeletonLoading"></span>
                                              </div>
                                          </div>
                                      </div>
                                  </div>
                              ))
                            : paginatedHotels.map((hotel, index) => (
                                  <HotelCard key={index} hotel={hotel} />
                              ))}
                    </div>
                    <nav
                        className="d-flex my-16 justify-content-center"
                        aria-label="navigation"
                    >
                        {totalPages > 1 && (
                            <ul className="flex gap-2 items-center">
                                <li
                                    className={`${
                                        page === 1 ? "disabled" : ""
                                    }`}
                                    onClick={() => handlePageChange(page - 1)}
                                >
                                    <span className="group page-link px-2 py-2 border-main-color  rounded-md  text-main-color hover:bg-main-color hover:text-white">
                                        <FaAngleLeft className="text-main-color group-hover:text-white duration-100 cursor-pointer " />
                                    </span>
                                </li>
                                {renderPagination}
                                <li
                                    className={`${
                                        page === totalPages ? "disabled" : ""
                                    }`}
                                    onClick={() => handlePageChange(page + 1)}
                                >
                                    <span className="group page-link px-2 py-2 border-main-color  rounded-md  text-main-color hover:bg-main-color hover:text-white">
                                        <FaAngleRight className="text-main-color group-hover:text-white duration-100 cursor-pointer " />
                                    </span>
                                </li>
                            </ul>
                        )}
                    </nav>
                </div>
            )}
            {availabilityResults?.hotels && (
                <MobileFilters
                    isOpen={isOpenFilters}
                    toggle={toggleFilters}
                    setFilteredHotels={setFilteredHotels}
                />
            )}
        </section>
    );
};

export default HotelSearchWrapper;
